import PropTypes from "prop-types";
import React, { useRef, useState } from "react";

import { useResizeObserver } from "@swa-ui/browser";
import { Area, CaptionPointer, CloseButton, Heading, Icon, Text } from "@swa-ui/core";
import i18n from "@swa-ui/locale";

import styles from "./DotcomHeaderMobileLoginPrompt.module.scss";

const POINTER_HEIGHT = 12;
const POINTER_WIDTH = 24;

/**
 * DotcomHeaderMobileLoginPrompt prompts the user to login, just below the
 * mobile global navigation. The component uses a pointer to indicate where the
 * user should click to login.
 */
export const DotcomHeaderMobileLoginPrompt = (props) => {
  const { loginButtonRef, onClose } = props;
  const [revealed, setRevealed] = useState(true);
  const containerRef = useRef(null);
  const [pointerOffset, setPointerOffset] = useState(0);

  useResizeObserver({
    callback: resetPointerOffset,
    element: containerRef,
  });

  return (
    <>
      {renderPointer()}
      <Area revealed={revealed}>
        <div className={styles.container} ref={containerRef}>
          <CloseButton className={styles.close} onClick={handleClose} />
          <div className={styles.content}>
            <div className={styles.heading}>
              <Icon
                name="Passenger"
                className={styles.icon}
                color="secondary-header-login-prompt-icon-color"
                size="24"
              />
              <Heading styleLevel={4} className={styles.headingText}>
                {i18n("DotcomHeaderMobileLoginPrompt__TITLE")}
              </Heading>
            </div>
            <Text color="primary-200" className={styles.text}>
              {i18n("DotcomHeaderMobileLoginPrompt__BODY")}
            </Text>
          </div>
        </div>
      </Area>
    </>
  );

  function renderPointer() {
    return (
      revealed && (
        <div className={styles.pointer} style={getPointerOffsets()}>
          <CaptionPointer direction="up" />
        </div>
      )
    );
  }

  function resetPointerOffset() {
    const loginButtonRect = loginButtonRef?.current?.getBoundingClientRect() ?? {};
    const loginButtonWidth = loginButtonRect.width ?? 0;
    const loginButtonOffset = loginButtonRect.left ?? 0;

    setPointerOffset(loginButtonOffset + loginButtonWidth / 2 - POINTER_WIDTH / 2);
  }

  function getPointerOffsets() {
    return {
      left: pointerOffset,
      top: (containerRef?.current?.offsetTop ?? 0) - POINTER_HEIGHT,
    };
  }

  function handleClose() {
    setRevealed(false);
    onClose?.();
  }
};

DotcomHeaderMobileLoginPrompt.displayName = "DotcomHeaderMobileLoginPrompt";
DotcomHeaderMobileLoginPrompt.propTypes = {
  /** Ref of the login button to point to with the caption-style pointer. */
  loginButtonRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),

  /** Callback to be triggered when the prompt is closed. */
  onClose: PropTypes.func,
};
